
import React from 'react';

const GameTips = () => {
  // SVG Icon components
  const TipIcon = ({ type }) => {
    // Different SVG icons for different tip types
    switch(type) {
      case 'combat':
        return (
          <svg viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg" className="w-8 h-8">
            <rect x="10" y="15" width="20" height="5" rx="1" fill="#8B5CF6"/>
            <rect x="15" y="10" width="3" height="15" rx="1" fill="#7E69AB"/>
            <rect x="28" y="16" width="5" height="3" fill="#9b87f5"/>
          </svg>
        );
      case 'time':
        return (
          <svg viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg" className="w-8 h-8">
            <circle cx="20" cy="20" r="15" fill="none" stroke="#8B5CF6" strokeWidth="2"/>
            <path d="M20 10 L20 20 L27 24" stroke="#7E69AB" strokeWidth="2" fill="none"/>
            <circle cx="20" cy="20" r="2" fill="#9b87f5"/>
          </svg>
        );
      case 'mission':
        return (
          <svg viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg" className="w-8 h-8">
            <rect x="10" y="10" width="20" height="25" rx="2" fill="none" stroke="#8B5CF6" strokeWidth="2"/>
            <path d="M15 18 L18 21 L25 14" stroke="#7E69AB" strokeWidth="2" fill="none"/>
            <path d="M15 25 L25 25" stroke="#9b87f5" strokeWidth="2"/>
            <path d="M15 30 L25 30" stroke="#9b87f5" strokeWidth="2"/>
          </svg>
        );
      case 'movement':
        return (
          <svg viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg" className="w-8 h-8">
            <polygon points="20,5 30,20 20,35 10,20" fill="none" stroke="#8B5CF6" strokeWidth="2"/>
            <circle cx="20" cy="20" r="3" fill="#7E69AB"/>
            <path d="M13 27 L27 13" stroke="#9b87f5" strokeWidth="2"/>
          </svg>
        );
      case 'strategy':
        return (
          <svg viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg" className="w-8 h-8">
            <rect x="10" y="10" width="20" height="20" rx="2" fill="none" stroke="#8B5CF6" strokeWidth="2"/>
            <circle cx="15" cy="15" r="2" fill="#7E69AB"/>
            <circle cx="25" cy="15" r="2" fill="#7E69AB"/>
            <circle cx="15" cy="25" r="2" fill="#7E69AB"/>
            <circle cx="25" cy="25" r="2" fill="#7E69AB"/>
            <path d="M15 15 L25 25 M15 25 L25 15" stroke="#9b87f5" strokeWidth="1"/>
          </svg>
        );
      default:
        return (
          <svg viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg" className="w-8 h-8">
            <circle cx="20" cy="20" r="15" fill="none" stroke="#8B5CF6" strokeWidth="2"/>
            <circle cx="20" cy="15" r="2" fill="#7E69AB"/>
            <path d="M20 20 L20 30" stroke="#9b87f5" strokeWidth="2"/>
          </svg>
        );
    }
  };

  const tips = [
    {
      id: 1,
      type: 'combat',
      title: "Headshots Are Critical",
      description: "Always aim for the head, especially during time slowdown. Headshots are instant eliminations in Time Shooter 3 Unblocked and conserve valuable ammunition."
    },
    {
      id: 2,
      type: 'time',
      title: "Micro Time Management",
      description: "Instead of using the entire time gauge at once, use brief pulses of slowdown for critical moments. This extends the utility of your time ability throughout longer engagements."
    },
    {
      id: 3,
      type: 'mission',
      title: "Prioritize Mission Objectives",
      description: "While eliminating enemies is important, focus on completing primary objectives first. Some missions have time limits that require efficient progression."
    },
    {
      id: 4,
      type: 'movement',
      title: "Use Environmental Cover",
      description: "Always move from cover to cover. The game features destructible environments, so be aware that wooden barriers and thin walls won't protect you for long."
    },
    {
      id: 5,
      type: 'strategy',
      title: "Listen for Audio Cues",
      description: "Enemy footsteps, reloading sounds, and radio chatter can alert you to nearby threats. Playing with headphones significantly improves your tactical awareness."
    },
    {
      id: 6,
      type: 'combat',
      title: "Master the Tactical Reload",
      description: "Reload your weapons when at safe positions, not during active firefights. Time slowdown can give you a window to reload safely in otherwise dangerous situations."
    },
    {
      id: 7,
      type: 'time',
      title: "Time-Based Grenade Throws",
      description: "Use time slowdown to precisely place grenades, especially around corners or through small openings. This lets you clear rooms before physically entering them."
    },
    {
      id: 8,
      type: 'movement',
      title: "Tactical Sliding",
      description: "The slide maneuver (SHIFT+SPACE) can be combined with time slowdown for rapid repositioning while maintaining accuracy. Perfect for crossing open areas under fire."
    },
    {
      id: 9,
      type: 'strategy',
      title: "Weapon Switching Strategy",
      description: "Switching weapons is faster than reloading. In intense firefights, switch to your sidearm instead of reloading your primary weapon when your magazine runs empty."
    },
    {
      id: 10,
      type: 'mission',
      title: "Secret Mission Objectives",
      description: "Some missions contain hidden secondary objectives that unlock new weapons or equipment. Explore thoroughly and look for computer terminals and documents that can be interacted with."
    }
  ];

  return (
    <section className="my-16" id="game-tips">
      <div className="max-w-4xl mx-auto">
        <h2 className="text-3xl font-bold mb-8 text-center text-purple-300">Pro Tips & Strategies</h2>
        
        <p className="text-center text-xl text-indigo-100 mb-10">
          Master Time Shooter 3 Unblocked with these expert tips and strategies from experienced players.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {tips.map((tip) => (
            <div key={tip.id} className="bg-indigo-900/30 p-5 rounded-lg border border-indigo-800/50 hover:border-purple-500/50 transition-colors duration-300">
              <div className="flex items-start gap-4">
                <div className="bg-indigo-800/50 p-2 rounded-md flex items-center justify-center">
                  <TipIcon type={tip.type} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-purple-300 mb-2">{tip.title}</h3>
                  <p className="text-indigo-100">{tip.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        <div className="mt-12 bg-gradient-to-r from-purple-900/50 via-indigo-900/50 to-purple-900/50 p-8 rounded-lg">
          <h3 className="text-2xl font-bold mb-4 text-center text-purple-300">Advanced Time Manipulation Techniques</h3>
          
          <p className="text-lg mb-6 text-indigo-100">
            The true mastery of Time Shooter 3 Unblocked comes from understanding the nuances of time manipulation. Here are some advanced techniques that separate novice players from experts:
          </p>
          
          <div className="space-y-5">
            <div className="bg-indigo-800/30 p-4 rounded-lg">
              <h4 className="text-xl font-semibold mb-2 text-purple-300">Time Pulsing</h4>
              <p className="text-indigo-100">
                Instead of holding down the time slowdown button continuously, tap it repeatedly in short bursts. This technique, known as "pulsing," allows you to slow time at critical moments while conserving your time gauge. Perfect for handling multiple enemies in sequence.
              </p>
            </div>
            
            <div className="bg-indigo-800/30 p-4 rounded-lg">
              <h4 className="text-xl font-semibold mb-2 text-purple-300">Bullet Dodging</h4>
              <p className="text-indigo-100">
                At higher difficulty levels, enemy bullets can be fatal even with armor. With practice, you can activate time slowdown when you see muzzle flashes and physically dodge incoming bullets by strafing. This technique requires precise timing but can save you in otherwise impossible situations.
              </p>
            </div>
            
            <div className="bg-indigo-800/30 p-4 rounded-lg">
              <h4 className="text-xl font-semibold mb-2 text-purple-300">Time-Focus Chaining</h4>
              <p className="text-indigo-100">
                Each successful headshot during time slowdown extends your time gauge slightly. By chaining consecutive headshots, you can maintain slowdown for extended periods. This technique rewards precision and can turn the tide in overwhelming encounters.
              </p>
            </div>
          </div>
          
          <div className="mt-8 text-center">
            <a href="#play-now" className="inline-block bg-purple-600 hover:bg-purple-700 text-white font-bold py-3 px-8 rounded-full transition-all transform hover:scale-105 hover:shadow-lg">
              Practice These Techniques Now
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default GameTips;
